import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { Currency, CurrencyState, CurrencyContextType, DEFAULT_CURRENCY, ExchangeRates } from '../types/currency';
import { currencyService } from '../services/currencyService';

// Action types
type CurrencyAction =
  | { type: 'SET_CURRENCY'; payload: Currency }
  | { type: 'SET_RATES'; payload: { rates: ExchangeRates; timestamp: Date } }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: CurrencyState = {
  selectedCurrency: DEFAULT_CURRENCY,
  exchangeRates: {},
  baseCurrency: 'AED',
  lastUpdated: null,
  isLoading: false,
  error: null,
};

// Reducer
function currencyReducer(state: CurrencyState, action: CurrencyAction): CurrencyState {
  switch (action.type) {
    case 'SET_CURRENCY':
      return {
        ...state,
        selectedCurrency: action.payload,
        error: null,
      };
    case 'SET_RATES':
      return {
        ...state,
        exchangeRates: action.payload.rates,
        lastUpdated: action.payload.timestamp,
        isLoading: false,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

// Create context
const CurrencyContext = createContext<CurrencyContextType | undefined>(undefined);

// Provider component
interface CurrencyProviderProps {
  children: React.ReactNode;
}

export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(currencyReducer, initialState);

  // Load saved currency preference from localStorage
  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem('selectedCurrency');
      if (savedCurrency) {
        const currency = JSON.parse(savedCurrency);
        dispatch({ type: 'SET_CURRENCY', payload: currency });
      }
    } catch (error) {
      console.error('Failed to load saved currency preference:', error);
    }
  }, []);

  // Fetch exchange rates on component mount and when base currency changes
  useEffect(() => {
    refreshRates();
  }, [state.baseCurrency]);

  // Save currency preference to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('selectedCurrency', JSON.stringify(state.selectedCurrency));
    } catch (error) {
      console.error('Failed to save currency preference:', error);
    }
  }, [state.selectedCurrency]);

  const refreshRates = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_ERROR' });

    try {
      const rates = await currencyService.getExchangeRates(state.baseCurrency);
      dispatch({
        type: 'SET_RATES',
        payload: {
          rates,
          timestamp: new Date(),
        },
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch exchange rates';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      console.error('Error fetching exchange rates:', error);
    }
  }, [state.baseCurrency]);

  const changeCurrency = useCallback((currency: Currency) => {
    dispatch({ type: 'SET_CURRENCY', payload: currency });
  }, []);

  const convertPrice = useCallback(
    (amount: number, fromCurrency: string = state.baseCurrency): number => {
      return currencyService.convertCurrency(
        amount,
        fromCurrency,
        state.selectedCurrency.code,
        state.exchangeRates
      );
    },
    [state.selectedCurrency.code, state.exchangeRates, state.baseCurrency]
  );

  const formatPrice = useCallback(
    (amount: number, fromCurrency: string = state.baseCurrency): string => {
      const convertedAmount = convertPrice(amount, fromCurrency);
      return currencyService.formatCurrency(
        convertedAmount,
        state.selectedCurrency.code,
        state.selectedCurrency.symbol
      );
    },
    [convertPrice, state.selectedCurrency]
  );

  const contextValue: CurrencyContextType = {
    currencyState: state,
    changeCurrency,
    convertPrice,
    formatPrice,
    refreshRates,
  };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

// Custom hook to use currency context
export const useCurrency = (): CurrencyContextType => {
  const context = useContext(CurrencyContext);
  if (context === undefined) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

// Export context for testing purposes
export { CurrencyContext };